export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export type FetcherOptions = RequestInit & {
  /** Abort after this many ms. Default 8s. */
  timeoutMs?: number;
};

export async function fetcher<T>(input: string, opts: FetcherOptions = {}): Promise<T> {
  const { timeoutMs = 8_000, signal: callerSignal, headers, ...rest } = opts;

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort('timeout'), timeoutMs);

  // Bridge an externally provided signal to our controller. Capture the handler
  // so it can be detached in `finally` — and add it `{ once: true }` — otherwise
  // every fetcher call that shares or outlives its caller signal leaks a listener
  // (each retaining this request's `controller` closure).
  let onCallerAbort: (() => void) | undefined;
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else {
      onCallerAbort = (): void => controller.abort(callerSignal.reason);
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  try {
    const res = await fetch(input, {
      ...rest,
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...headers,
      },
    });

    const contentType = res.headers.get('content-type') ?? '';
    const isJson = contentType.includes('application/json');
    const body: unknown = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      throw new ApiError(`Request failed with ${res.status}`, res.status, body);
    }
    return body as T;
  } finally {
    window.clearTimeout(timeoutId);
    if (callerSignal && onCallerAbort) callerSignal.removeEventListener('abort', onCallerAbort);
  }
}
